// ── 로그 관리 — 포맷터 & 유틸 함수 ────────────────────────────────────────────
import { toKoreanDateTimeString } from '../../../utils/date';

// ── 상태코드 색상 → { bg, text } ────────────────────────────────────────────
export function statusColor(code) {
  const n = Number(code);
  if (n >= 200 && n < 300) return { bg: '#f0fdf4', text: '#15803d' };
  if (n >= 300 && n < 400) return { bg: '#f0f9ff', text: '#0369a1' };
  if (n >= 400 && n < 500) return { bg: '#fefce8', text: '#a16207' };
  if (n >= 500)             return { bg: '#fef2f2', text: '#dc2626' };
  return { bg: 'var(--bg-subtle)', text: 'var(--text-sub)' };
}

// ── HTTP 메서드 색상 → { bg, text } ─────────────────────────────────────────
export function methodColor(method) {
  const m = String(method || '').toUpperCase();
  if (m === 'GET')    return { bg: '#f0fdf4', text: '#15803d' };
  if (m === 'POST')   return { bg: '#eff6ff', text: '#1d4ed8' };
  if (m === 'PUT')    return { bg: '#fff7ed', text: '#c2410c' };
  if (m === 'PATCH')  return { bg: '#fefce8', text: '#a16207' };
  if (m === 'DELETE') return { bg: '#fef2f2', text: '#dc2626' };
  return { bg: 'var(--bg-subtle)', text: 'var(--text-sub)' };
}

// ── 날짜/시간 한국어 포맷 ─────────────────────────────────────────────────────
export function fmtDatetime(val) {
  // toKoreanDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
  return toKoreanDateTimeString(val);
}

// ── 숫자 천 단위 콤마 포맷 ────────────────────────────────────────────────────
export function fmtNum(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('ko-KR');
}

// ── 경로 축약 (쿼리스트링 제거 + 말줄임표) ─────────────────────────────────────
export function shortPath(path) {
  if (!path) return '-';
  const idx = path.indexOf('?');
  return idx > -1 ? path.slice(0, idx) + '…' : path;
}

// ── request_body 필드 → 한국어 라벨 매핑 ─────────────────────────────────────
export const BODY_FIELD_LABELS = {
  company_name:         '회사명',
  ceo_name:             '대표자명',
  phone:                '전화번호',
  mobile_phone:         '휴대폰',
  address:              '주소',
  address_detail:       '주소 상세',
  region:               '지역',
  industry:             '업종',
  visit_date:           '방문일',
  visit_time:           '방문시간',
  contract_date:        '계약일',
  sales_person_name:    '마케팅담당자',
  user_name:            '담당 TM',
  ticket_type:          '상품',
  contract_ticket_type: '계약상품',
  service_ticket_type:  '서비스상품',
  ticket_count:         '계약장수',
  service_ticket_count: '서비스장수',
  feedback:             '피드백',
  feedback_memo:        '피드백 메모',
  keep_memo:            '킵 메모',
  content:              '상담내용',
  is_contracted:        '계약완료',
  is_keep:              '킵여부',
  name:                 '이름',
  login_id:             '아이디',
  worker_type:          '직원유형',
  role:                 '권한',
  leave_date:           '연차날짜',
  leave_type:           '연차종류',
  status:               '상태',
  ip_address:           'IP주소',
  workflow_step_id:     '업무단계 ID',
  promotional_order_id: '홍보물주문 ID',
};

// ── request_body 객체를 한국어 라벨+값 배열로 변환 (표시용) ─────────────────
export function formatBodyRows(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return [];
  return Object.entries(body)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({
      key:   k,
      label: BODY_FIELD_LABELS[k] || k, // 한국어 라벨 없으면 원본 키 표시
      value: typeof v === 'boolean'
        ? (v ? '예' : '아니오')
        : Array.isArray(v)
          ? `[${v.length}건] ${JSON.stringify(v)}`
          : typeof v === 'object'
            ? JSON.stringify(v)
            : String(v),
    }));
}

// ── 경로 → 기능 배지 정보 { label, color } 반환 ────────────────────────────
export function resolveFeatureLabel(path) {
  const p = String(path || '');
  if (/\/api\/attendance\/check-in/.test(p))   return { label: '출근',       color: '#16a34a' };
  if (/\/api\/attendance\/check-out/.test(p))  return { label: '퇴근',       color: '#2563eb' };
  if (/\/api\/attendance/.test(p))             return { label: '출퇴근',     color: '#0369a1' };
  if (/\/api\/(admin\/)?leaves/.test(p))       return { label: '연차',       color: '#7c3aed' };
  if (/\/api\/(admin\/)?tm\/meeting-map/.test(p)) return { label: '미팅지도', color: '#b45309' };
  if (/\/api\/(admin\/)?tm\/calls/.test(p))    return { label: '일일콜',     color: '#b45309' };
  if (/\/api\/(admin\/)?contracts/.test(p))    return { label: '계약',       color: '#0f766e' };
  if (/\/api\/admin\/promotional/.test(p))     return { label: '홍보물',     color: '#9333ea' };
  if (/\/api\/admin\/users/.test(p))           return { label: '직원관리',   color: 'var(--text-sub)' };
  // ips · ip-scope(2026-10-23 출퇴근 관리 ☰ IP 창) · ip-aliases
  if (/\/api\/admin\/ip(s\b|-)/.test(p))       return { label: 'IP관리',     color: 'var(--text-sub)' };
  if (/\/api\/admin\/attendance/.test(p))      return { label: '출퇴근관리', color: '#0369a1' };
  if (/\/api\/admin\/leaves/.test(p))          return { label: '연차관리',   color: '#7c3aed' };
  if (/\/api\/(login|logout|auth)/.test(p))    return { label: '로그인/아웃', color: 'var(--text-muted)' };
  if (/\/api\/me/.test(p))                     return { label: '내 정보',    color: 'var(--text-muted)' };
  // 외부 회사별 기능 경로
  if (/\/api\/admin\/ext\/[^/]+\/users/.test(p))      return { label: '외부직원관리', color: '#0f766e' };
  if (/\/api\/admin\/ext\/[^/]+\/attendance/.test(p)) return { label: '외부출퇴근',   color: '#0369a1' };
  if (/\/api\/admin\/ext\/[^/]+\/leaves/.test(p))     return { label: '외부연차',     color: '#7c3aed' };
  if (/\/api\/admin\/ext\/[^/]+\/tm-calls/.test(p))   return { label: '외부일일콜',   color: '#b45309' };
  if (/\/api\/admin\/external-companies/.test(p))     return { label: '외부회사설정', color: 'var(--text-sub)' };
  return null;
}
